/**
 * CrearTareaModal — pedir / crear una tarea nueva.
 *
 * Rehecho para el celular (SOA 10 PRO):
 *   · Pantalla completa en el celular, con el botón «Crear tarea» siempre a la vista (barra inferior fija).
 *   · Lo esencial arriba, en el orden en que se piensa: cliente → tipo → qué hay que hacer → para cuándo → prioridad → brief.
 *     El nombre con la convención YYYYMMDD_Cliente_Título se arma solo (antes había que escribirlo a mano).
 *   · «Para cuándo»: entrega al cliente (fecha + hora, con atajos) y fecha de publicación, siempre a la vista. Cuándo se trabaja la pieza no se pregunta: lo define la asignación.
 *   · Brief por voz (dictado) y borrador guardado: si cerrás sin querer, lo que escribiste vuelve.
 *   · Todo lo demás (grupo, asignar, horas estimadas, canales) en «Más opciones».
 *   · Al guardar espera a Drive y muestra la pantalla de éxito con la Ficha Técnica y las carpetas.
 */
import { useState, useEffect, useMemo, useRef } from "react";
import TipoIcono from "./TipoIcono.jsx";
import {
  X, Plus, Sparkles, AlertCircle, Loader2, CheckCircle2, Copy, ExternalLink,
  CalendarDays, Users, Flag, Tag, Hash, Building2, FileText, FolderOpen, ChevronDown, Icon,
} from "../lib/icons.jsx";
import { api } from "../api/client.js";
import { cn, combinarFechaHoraPY } from "../lib/utils.js";
import { Button } from "./ui/button.jsx";
import { Input } from "./ui/input.jsx";
import { useClient } from "../context/ClientContext.jsx";
import { useToast } from "./Toast.jsx";
import ChannelPicker from "./ChannelPicker.jsx";
import ClienteComboBox from "./ClienteComboBox.jsx";
import { CrearGrupoModal } from "./CrearGrupoModal.jsx";
import { FeeLinea } from "./FeeMedidor.jsx";
import { useFlag } from "../lib/useFlags.js";

// v8.3.8 · Nomenclatura unificada (spec del equipo)
const TIPOS = ["Reel", "Gráfica", "Carrusel", "Historia", "Fotos", "Cobertura", "Documento", "Presentación"];

const PRIORIDADES = [
  { value: "baja",    label: "Baja",    cls: "bg-[#CFFAFE] text-[#155E75]" },
  { value: "media",   label: "Media",   cls: "bg-[#FEF3C7] text-[#92400E]" },
  { value: "alta",    label: "Alta",    cls: "bg-[#FDE68A] text-[#78350F]" },
  { value: "urgente", label: "Urgente", cls: "bg-[#FEE2E2] text-[#B91C1C]" },
];

const ROLES_ASIGNAR = ["EC", "COM", "CCC", "SMM", "CPC", "Diseñador", "Audiovisual", "Fotógrafo"];
const HORAS_ATAJO = ["10:00", "14:00", "18:00"];
const CLAVE_BORRADOR = "crew_nueva_tarea_borrador_v1";

// Regla del OS (backend: src/lib/fechas-tarea.js): la entrega va 3 días antes de la publicación y nunca en el pasado.
// El backend también la aplica si llega solo la publicación; acá se calcula para que quien crea la vea en pantalla.
const DIAS_ANTICIPACION_ENTREGA = 3;
const hoyPY = () => new Date(Date.now() - 3 * 3600 * 1000).toISOString().slice(0, 10);
const sumarDias = (iso, n) => { const d = new Date(iso + "T12:00:00Z"); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
function entregaDesdePublicacion(pub) {
  if (!pub) return "";
  const hoy = hoyPY();
  const ent = sumarDias(pub, -DIAS_ANTICIPACION_ENTREGA);
  return ent >= hoy ? ent : (pub >= hoy ? hoy : pub);
}
function proximoViernes() {
  const h = hoyPY();
  const dia = new Date(h + "T12:00:00Z").getUTCDay();            // 0 dom … 5 vie
  const falta = (5 - dia + 7) % 7;
  return sumarDias(h, falta === 0 ? 0 : falta);
}
function fechaCorta(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T12:00:00Z");
  return d.toLocaleDateString("es-PY", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
}

// Misma convención que el backend (lib/nombre-tarea.js): cliente en PascalCase sin espacios ni acentos.
function compactCliente(nombre) {
  return String(nombre || "").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-zA-Z0-9 ]/g, " ")
    .split(/\s+/).filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join("");
}
function armarNombre({ titulo, cliente, fecha }) {
  const t = String(titulo || "").replace(/[\r\n\t|]/g, " ").replace(/\s+/g, " ").replace(/^_+|_+$/g, "").trim();
  if (!t) return "";
  if (/^\d{8}_/.test(t)) return t;                                   // ya viene con la convención
  const f = String(fecha || "").replace(/-/g, "").slice(0, 8);
  const c = compactCliente(cliente);
  return [f, c, t].filter(Boolean).join("_");
}

function Chip({ activo, onClick, children, className }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={!!activo}
      className={cn("h-11 px-4 rounded-full text-[15px] font-extrabold whitespace-nowrap transition-colors",
        activo ? "bg-foreground text-background" : "bg-muted text-foreground active:bg-muted/70", className)}>
      {children}
    </button>
  );
}

export function CrearTareaModal() {
  const feeVivoOn = useFlag("ff_fee_vivo");
  const { selectedId, clientes } = useClient();
  const { toast } = useToast();

  const [open,    setOpen]    = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [success, setSuccess] = useState(null);   // { tarea, drive_ready }
  const [mas, setMas] = useState(false);
  const [dictando, setDictando] = useState(false);
  const reconRef = useRef(null);

  function emptyForm(clienteId) {
    return {
      titulo:           "",
      cliente_id:       clienteId || "",
      grupo_id:         "",
      tipo_contenido:   "",
      estado:           "pendiente",
      prioridad:        "media",
      asignar_a:        "",
      deadline:         "",
      deadline_auto:    true,       // la entrega se completa sola desde la publicación hasta que alguien la toca
      deadline_hora:    "",
      fecha_publicacion:"",
      horas_estimadas:  "",
      brief_url:        "",
      redes_seleccionadas: [],
      ia_briefing:      "",
    };
  }
  const [form, setForm] = useState(() => emptyForm(selectedId));

  // Grupos del cliente elegido (se recargan al abrir y al cambiar de cliente)
  const [grupos, setGrupos] = useState([]);
  const [grupoModalOpen, setGrupoModalOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    if (!form.cliente_id) { setGrupos([]); return; }
    api.getGrupos({ cliente_id: form.cliente_id })
      .then(r => setGrupos(r.grupos || r || []))
      .catch(() => setGrupos([]));
  }, [form.cliente_id, open]);

  function handleGrupoCreated(nuevo) {
    if (nuevo?.id) {
      setGrupos(g => [...g, nuevo]);
      setField("grupo_id", nuevo.id);
      toast.success(`Grupo "${nuevo.nombre}" creado`);
    }
    setGrupoModalOpen(false);
  }

  // Apertura: desde el botón «+» / ⌘K. El precompletado de ⌘K («Crear tarea …») se aplica una sola vez;
  // sin precompletado, vuelve el borrador de las últimas 24 h si había.
  useEffect(() => {
    const handler = (e) => {
      if (e.detail !== "new-task") return;
      const pre = window.__crewPrefill; window.__crewPrefill = null;
      let base = emptyForm(pre?.cliente_id || selectedId);
      if (pre) {
        const p = Object.fromEntries(Object.entries(pre).filter(([, v]) => v));
        if (p.nombre && !p.titulo) { p.titulo = p.nombre; delete p.nombre; }
        base = { ...base, ...p };
      } else {
        try {
          const b = JSON.parse(localStorage.getItem(CLAVE_BORRADOR) || "null");
          if (b && Date.now() - b.ts < 24 * 3600 * 1000) base = { ...base, ...b.form, cliente_id: b.form.cliente_id || base.cliente_id };
        } catch { /* sin storage */ }
      }
      setForm(base);
      setMas(false);
      setError(null); setSuccess(null);
      setOpen(true);
    };
    window.addEventListener("crew-action", handler);
    return () => window.removeEventListener("crew-action", handler);
  }, [selectedId]);

  useEffect(() => {
    if (open && selectedId && !form.cliente_id) setForm(f => ({ ...f, cliente_id: selectedId }));
  }, [selectedId, open, form.cliente_id]);

  // Borrador
  useEffect(() => {
    if (!open || success) return;
    const hay = form.titulo || form.ia_briefing;
    try {
      if (hay) localStorage.setItem(CLAVE_BORRADOR, JSON.stringify({ ts: Date.now(), form }));
    } catch { /* sin storage */ }
  }, [form, open, success]);

  function setField(k, v) { setForm(f => ({ ...f, [k]: v, ...(k === "deadline" ? { deadline_auto: false } : {}) })); }
  // Al elegir la publicación, la entrega se completa con la regla (3 días antes), salvo que ya la hayan fijado a mano.
  function setPublicacion(v) {
    setForm(f => ({ ...f, fecha_publicacion: v, ...(f.deadline_auto !== false && v ? { deadline: entregaDesdePublicacion(v) } : {}) }));
  }

  function handleClose() {
    if (saving) return;
    try { reconRef.current?.stop?.(); } catch { /* sin dictado */ }
    setOpen(false); setError(null); setSuccess(null);
  }

  const clienteSel = clientes.find(c => c.id === form.cliente_id);
  const fechaNombre = form.fecha_publicacion || form.deadline || hoyPY();
  const nombreFinal = useMemo(
    () => armarNombre({ titulo: form.titulo, cliente: clienteSel?.nombre, fecha: fechaNombre }),
    [form.titulo, clienteSel?.nombre, fechaNombre]
  );
  // Cuándo tiene que estar lista (deadline) y cuándo se publica son obligatorias: de ellas salen el límite interno y la fecha de tarea (por capacidad).
  const listo = !!form.cliente_id && form.titulo.trim().length >= 3 && !!form.deadline && !!form.fecha_publicacion;

  // Dictado del brief (Android / Chrome / Safari recientes)
  const hayDictado = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  function alternarDictado() {
    if (dictando) { try { reconRef.current?.stop(); } catch { /* ya parado */ } return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.lang = "es-PY"; r.interimResults = false; r.continuous = true;
    r.onresult = (ev) => {
      const t = Array.from(ev.results).slice(ev.resultIndex).map(x => x[0].transcript).join(" ").trim();
      if (t) setForm(f => ({ ...f, ia_briefing: (f.ia_briefing ? f.ia_briefing.trimEnd() + " " : "") + t }));
    };
    r.onend = () => setDictando(false);
    r.onerror = () => setDictando(false);
    reconRef.current = r;
    try { r.start(); setDictando(true); } catch { setDictando(false); }
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!form.cliente_id) { setError("Elegí el cliente"); return; }
    if (form.titulo.trim().length < 3) { setError("Contá en pocas palabras qué hay que hacer"); return; }
    if (!form.deadline) { setError("Falta cuándo tiene que estar lista la tarea (deadline)"); return; }
    if (!form.fecha_publicacion) { setError("Falta la fecha de publicación"); return; }

    setSaving(true);
    setError(null);
    const toastId = toast.loading("Creando tarea y carpetas de Drive…", { description: "Esto puede tardar unos segundos." });

    try {
      const res = await api.createTarea({
        nombre:            nombreFinal,
        cliente_id:        form.cliente_id,
        grupo_id:          form.grupo_id || null,
        tipo_contenido:    form.tipo_contenido || null,
        estado:            form.estado,
        prioridad:         form.prioridad,
        asignar_a:         form.asignar_a || null,
        deadline:          combinarFechaHoraPY(form.deadline, form.deadline_hora),
        fecha_entrega:     form.deadline || null,
        fecha_publicacion: form.fecha_publicacion || null,
        horas_estimadas:   form.horas_estimadas ? Number(form.horas_estimadas) : null,
        brief_url:         form.brief_url || null,
        redes_sociales:    form.redes_seleccionadas.join(", ") || null,
        ia_briefing:       form.ia_briefing || null,
        origen_chat_msg_id: form.origen_chat_msg_id || undefined,   // si nació de un mensaje del chat, queda vinculada (en los dos sentidos)
      });
      toast.update(toastId, "success", "Tarea creada", { description: res.drive_ready ? "Drive listo ✓" : "Drive creándose en background…" });
      window.dispatchEvent(new CustomEvent("crew-refresh", { detail: "tareas" }));
      try { localStorage.removeItem(CLAVE_BORRADOR); } catch { /* sin storage */ }
      setSuccess({ tarea: res.tarea || { id: res.id }, drive_ready: !!res.drive_ready });
    } catch (e) {
      setError(e.message);
      toast.update(toastId, "error", "Error al crear", { description: e.message });
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  const hoy = hoyPY(), manana = sumarDias(hoy, 1), viernes = proximoViernes();

  return (
    <div className="fixed inset-0 z-50 sm:flex sm:items-center sm:justify-center sm:p-4">
      <div className="fixed inset-0 bg-black/40 sm:backdrop-blur-sm" onClick={handleClose} />

      <div role="dialog" aria-modal="true" aria-labelledby="nueva-tarea-titulo"
           className="relative z-10 w-full h-full sm:h-auto sm:max-h-[92vh] sm:max-w-3xl flex flex-col bg-background sm:bg-white sm:rounded-3xl sm:shadow-2xl overflow-hidden">
        {/* Cabecera */}
        <div className="flex items-center gap-3 px-4 pt-[max(12px,env(safe-area-inset-top))] pb-3 sm:pt-4 border-b border-border bg-card flex-shrink-0">
          <div className={cn("w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0", success ? "bg-[#D1FAE5]" : "bg-[#CFFAFE]")}>
            {success ? <CheckCircle2 className="h-5 w-5 text-[#065F46]" /> : <Plus className="h-5 w-5 text-[#155E75]" />}
          </div>
          <div className="flex-1 min-w-0">
            <h2 id="nueva-tarea-titulo" className="text-[20px] font-extrabold leading-tight">{success ? "Tarea creada" : "Nueva tarea"}</h2>
            <p className="text-[13px] text-muted-foreground truncate">{success ? success.tarea.id : clienteSel ? `Para ${clienteSel.nombre}` : "Empezá por el cliente"}</p>
          </div>
          <button onClick={handleClose} disabled={saving} aria-label="Cerrar" className="h-11 w-11 rounded-full flex items-center justify-center text-muted-foreground active:bg-muted disabled:opacity-30">
            <X className="h-5 w-5" />
          </button>
        </div>

        {success ? (
          <div className="flex-1 overflow-y-auto">
            <SuccessPane tarea={success.tarea} driveReady={success.drive_ready}
                         onNueva={() => { setForm(emptyForm(form.cliente_id)); setSuccess(null); setMas(false); }}
                         onClose={handleClose} />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col">
            <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-6">
              {/* Escritorio: dos columnas (cliente · tipo · qué / cuándo · urgencia · brief); celular: una sola */}
              <div className="md:grid md:grid-cols-2 md:gap-x-8 md:items-start space-y-6 md:space-y-0">
              <div className="space-y-6">
              {/* 1 · Cliente */}
              <Bloque n="1" titulo="¿Para qué cliente?" icono={Building2}>
                <ClienteComboBox
                  clientes={clientes.filter(c => c.estado === "activo").sort((a, b) => a.nombre.localeCompare(b.nombre))}
                  value={form.cliente_id} onChange={v => setForm(f => ({ ...f, cliente_id: v, grupo_id: "" }))}
                  placeholder="Buscar cliente…" />
                {feeVivoOn && form.cliente_id && <div className="mt-2"><FeeLinea clienteId={form.cliente_id} creditos={1} /></div>}
              </Bloque>

              {/* 2 · Tipo */}
              <Bloque n="2" titulo="¿Qué tipo de pieza es?" icono={Tag}>
                <div className="grid grid-cols-4 gap-2">
                  {TIPOS.map(t => (
                    <button key={t} type="button" aria-pressed={form.tipo_contenido === t}
                      onClick={() => setField("tipo_contenido", form.tipo_contenido === t ? "" : t)}
                      className={cn("rounded-2xl py-2.5 px-1 flex flex-col items-center gap-1.5 text-[13px] font-extrabold leading-tight transition-colors",
                        form.tipo_contenido === t ? "bg-foreground text-background" : "bg-muted text-foreground active:bg-muted/70")}>
                      <TipoIcono tipo={t} tamano="md" />
                      <span className="truncate max-w-full">{t}</span>
                    </button>
                  ))}
                </div>
                {form.tipo_contenido && (
                  <p className="text-[13px] text-[#5B21B6] mt-2 flex items-center gap-1"><Sparkles className="h-3 w-3" /> Gemini estima las horas al crear</p>
                )}
              </Bloque>

              {/* 3 · Qué hay que hacer */}
              <Bloque n="3" titulo="¿Qué hay que hacer?" icono={Hash}>
                <input value={form.titulo} onChange={e => setField("titulo", e.target.value)}
                  placeholder="Ej: Reel aniversario con testimonios"
                  maxLength={120} enterKeyHint="next"
                  className="w-full h-14 px-4 rounded-2xl bg-white border-2 border-border text-[17px] font-semibold focus:outline-none focus:border-[#0E7490]" />
                {nombreFinal && (
                  <p className="text-[13px] text-muted-foreground mt-2 break-all">Se va a llamar <span className="font-mono text-foreground/80">{nombreFinal}</span></p>
                )}
              </Bloque>

              </div>
              <div className="space-y-6 mt-6 md:mt-0">
              {/* 4 · Fechas: entrega al cliente (deadline, con hora) y publicación. Cuándo se trabaja lo decide la asignación, no quien pide. */}
              <Bloque n="4" titulo="¿Para cuándo?" icono={CalendarDays}>
                <div className="text-[14px] font-extrabold text-foreground mb-2">Tiene que estar lista el <span className="text-[#B91C1C]" aria-hidden="true">*</span></div>
                <div className="flex flex-wrap gap-2">
                  <Chip activo={form.deadline === hoy} onClick={() => setField("deadline", form.deadline === hoy ? "" : hoy)}>Hoy</Chip>
                  <Chip activo={form.deadline === manana} onClick={() => setField("deadline", form.deadline === manana ? "" : manana)}>Mañana</Chip>
                  {viernes !== hoy && viernes !== manana && (
                    <Chip activo={form.deadline === viernes} onClick={() => setField("deadline", form.deadline === viernes ? "" : viernes)}>Viernes</Chip>
                  )}
                </div>
                <div className="mt-3 grid grid-cols-[1fr_auto] gap-2">
                  <input type="date" aria-label="Fecha de entrega al cliente" value={form.deadline} onChange={e => setField("deadline", e.target.value)}
                    className="w-full h-12 px-4 rounded-2xl bg-white border-2 border-border text-[16px] focus:outline-none focus:border-[#0E7490]" />
                  <input type="time" aria-label="Hora de entrega" value={form.deadline_hora || ""} onChange={e => setField("deadline_hora", e.target.value)}
                    disabled={!form.deadline} placeholder="18:00"
                    className="h-12 w-32 px-3 rounded-2xl bg-white border-2 border-border text-[16px] focus:outline-none focus:border-[#0E7490] disabled:opacity-50" />
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {HORAS_ATAJO.map(h => <Chip key={h} className="h-9 px-3 text-[13px]" activo={!!form.deadline && (form.deadline_hora || "18:00") === h} onClick={() => setField("deadline_hora", h)}>{h}</Chip>)}
                </div>
                <p className="text-[13px] text-muted-foreground mt-2">
                  {form.deadline ? <>Tiene que estar lista el <b className="text-foreground">{fechaCorta(form.deadline)}</b> a las <b className="text-foreground">{form.deadline_hora || "18:00"}</b>.</> : "Obligatorio. Elegí un atajo o una fecha."}
                </p>

                <div className="text-[14px] font-extrabold text-foreground mt-5 mb-2">Se publica el <span className="text-[#B91C1C]" aria-hidden="true">*</span></div>
                <div className="flex flex-wrap gap-2">
                  <Chip activo={!!form.deadline && form.fecha_publicacion === sumarDias(form.deadline, DIAS_ANTICIPACION_ENTREGA)}
                    onClick={() => form.deadline && setForm(f => ({ ...f, fecha_publicacion: f.fecha_publicacion === sumarDias(f.deadline, DIAS_ANTICIPACION_ENTREGA) ? "" : sumarDias(f.deadline, DIAS_ANTICIPACION_ENTREGA) }))}
                    className={cn(!form.deadline && "opacity-50")}>{DIAS_ANTICIPACION_ENTREGA} días después de la entrega</Chip>
                </div>
                <input type="date" aria-label="Fecha de publicación" value={form.fecha_publicacion} onChange={e => setPublicacion(e.target.value)}
                  className="mt-3 w-full h-12 px-4 rounded-2xl bg-white border-2 border-border text-[16px] focus:outline-none focus:border-[#0E7490]" />
                <p className="text-[13px] text-muted-foreground mt-2">
                  {form.deadline_auto !== false && form.fecha_publicacion && form.deadline
                    ? <>La entrega se completó sola: {DIAS_ANTICIPACION_ENTREGA} días antes de publicar. Si querés otra, elegila arriba.</>
                    : <>Si cargás solo la publicación, la entrega se completa sola {DIAS_ANTICIPACION_ENTREGA} días antes.</>}
                </p>
                {form.deadline && form.fecha_publicacion && form.fecha_publicacion < form.deadline && (
                  <p className="text-[13px] mt-2 text-[#92400E]">Ojo: se publica antes de la entrega al cliente.</p>
                )}
                <p className="text-[13px] text-muted-foreground mt-2">El día en que se trabaja la pieza lo calcula el sistema desde el deadline, según la carga de quien la tiene: no hace falta cargarlo.</p>
              </Bloque>

              {/* 5 · Prioridad */}
              <Bloque n="5" titulo="¿Qué tan urgente es?" icono={Flag}>
                <div className="grid grid-cols-4 gap-2">
                  {PRIORIDADES.map(p => (
                    <button key={p.value} type="button" aria-pressed={form.prioridad === p.value} onClick={() => setField("prioridad", p.value)}
                      className={cn("h-12 rounded-2xl text-[14px] font-extrabold transition-all", p.cls, form.prioridad === p.value ? "ring-2 ring-foreground ring-offset-2 ring-offset-background" : "opacity-70")}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </Bloque>

              {/* 6 · Brief */}
              <Bloque n="6" titulo="Contanos más (opcional)" icono={Sparkles}>
                <div className="relative">
                  <textarea rows={4} value={form.ia_briefing} onChange={e => setField("ia_briefing", e.target.value)}
                    placeholder="Idea, contexto, referencias… Si lo dejás vacío, Gemini lo expande."
                    className="w-full px-4 py-3 pr-14 rounded-2xl bg-white border-2 border-border text-[16px] leading-snug resize-none focus:outline-none focus:border-[#0E7490]" />
                  {hayDictado && (
                    <button type="button" onClick={alternarDictado} aria-label={dictando ? "Parar dictado" : "Dictar con la voz"}
                      className={cn("absolute right-2 bottom-2 h-11 w-11 rounded-full flex items-center justify-center", dictando ? "bg-[#DC2626] text-white animate-pulse" : "bg-foreground text-background")}>
                      <Icon name={dictando ? "stop" : "mic"} className="text-[22px]" />
                    </button>
                  )}
                </div>
                {dictando && <p className="text-[13px] text-[#B91C1C] font-semibold mt-1">Escuchando… tocá el botón para parar.</p>}
              </Bloque>

              </div>
              </div>

              {/* Más opciones */}
              <div className="rounded-3xl bg-card border border-border overflow-hidden">
                <button type="button" onClick={() => setMas(m => !m)} aria-expanded={mas}
                  className="w-full h-14 px-4 flex items-center gap-3 text-[16px] font-extrabold">
                  <span className="flex-1 text-left">Más opciones</span>
                  <span className="text-[13px] font-semibold text-muted-foreground">grupo · asignar · canales</span>
                  <ChevronDown className={cn("h-5 w-5 transition-transform", mas && "rotate-180")} />
                </button>
                {mas && (
                  <div className="px-4 pb-4 pt-1 space-y-5 border-t border-border">
                    <Campo etiqueta="Grupo destino" icono={FolderOpen} ayuda="Plan mensual, campaña, evento… Si lo dejás vacío se deriva del mes de publicación.">
                      <div className="flex gap-2">
                        <select value={form.grupo_id} onChange={e => setField("grupo_id", e.target.value)} disabled={!form.cliente_id} className={selectCls + " flex-1"}>
                          <option value="">{!form.cliente_id ? "Elegí un cliente primero" : grupos.length === 0 ? "Sin grupos · creá uno →" : "(sin grupo)"}</option>
                          {grupos.slice().sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "")).map(g => <option key={g.id} value={g.id}>{g.nombre}{g.tipo ? ` · ${g.tipo}` : ""}</option>)}
                        </select>
                        <button type="button" onClick={() => setGrupoModalOpen(true)} disabled={!form.cliente_id} aria-label="Crear grupo nuevo"
                          className="h-12 w-12 rounded-2xl border-2 border-border flex items-center justify-center disabled:opacity-40"><Plus className="h-5 w-5" /></button>
                      </div>
                    </Campo>

                    <Campo etiqueta="Asignar a (rol)" icono={Users} ayuda={form.asignar_a && clienteSel ? `Se avisa al ${form.asignar_a} de ${clienteSel.nombre}.` : "Si no elegís, la asignación es automática."}>
                      <div className="flex flex-wrap gap-2">
                        <Chip activo={!form.asignar_a} onClick={() => setField("asignar_a", "")}>Automático</Chip>
                        {ROLES_ASIGNAR.map(r => <Chip key={r} activo={form.asignar_a === r} onClick={() => setField("asignar_a", form.asignar_a === r ? "" : r)}>{r}</Chip>)}
                      </div>
                    </Campo>

                    {form.deadline && (
                      <p className="text-[13px] bg-[#FFFBEB] border border-[#FDE68A] rounded-2xl px-3 py-2 text-[#78350F]">
                        El límite interno del equipo se calcula solo: <b>{calcFechaLimite(form.deadline)}</b> <span className="opacity-70">(entrega − 48 h)</span>
                      </p>
                    )}

                    <Campo etiqueta="Horas estimadas" icono={Hash} ayuda="Vacío = automático según el tipo.">
                      <Input type="number" step="0.5" min="0" inputMode="decimal" value={form.horas_estimadas} onChange={e => setField("horas_estimadas", e.target.value)} placeholder="Auto" className="h-12 rounded-2xl text-[16px]" />
                    </Campo>

                    <Campo etiqueta="Canales" icono={Hash}>
                      <ChannelPicker value={form.redes_seleccionadas}
                        onChange={(v) => setForm(f => ({ ...f, redes_seleccionadas: Array.isArray(v) ? v : (v ? String(v).split(/[,;]+/).map(s => s.trim()).filter(Boolean) : []) }))} />
                    </Campo>
                  </div>
                )}
              </div>

              <p className="text-[13px] text-muted-foreground flex items-start gap-2 px-1">
                <Sparkles className="h-4 w-4 text-[#155E75] flex-shrink-0 mt-0.5" />
                <span>Al crear se arman solas las <b>carpetas en Drive</b> y la <b>Ficha Técnica</b>, y se avisa a quien corresponda.</span>
              </p>
            </div>

            {/* Barra inferior fija */}
            <div className="flex-shrink-0 border-t border-border bg-card px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] space-y-2">
              {error && (
                <div role="alert" className="flex items-start gap-2 px-3 py-2 rounded-2xl bg-[#FEE2E2] text-[#B91C1C] text-[14px] font-semibold">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />{error}
                </div>
              )}
              <div className="flex items-center gap-2">
                <button type="button" onClick={handleClose} disabled={saving} className="h-14 px-5 rounded-2xl bg-muted text-[16px] font-extrabold disabled:opacity-40">Cancelar</button>
                <button type="submit" disabled={saving || !listo}
                  className="flex-1 h-14 rounded-2xl bg-foreground text-background text-[17px] font-extrabold flex items-center justify-center gap-2 disabled:opacity-40">
                  {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
                  {saving ? "Creando…" : "Crear tarea"}
                </button>
              </div>
              {!listo && !saving && <p className="text-[12px] text-muted-foreground text-center">Falta {!form.cliente_id ? "elegir el cliente" : form.titulo.trim().length < 3 ? "contar qué hay que hacer" : !form.deadline ? "cuándo tiene que estar lista" : "la fecha de publicación"}.</p>}
            </div>
          </form>
        )}
      </div>

      {grupoModalOpen && (
        <CrearGrupoModal clientes={clientes} defaultClienteId={form.cliente_id} onClose={() => setGrupoModalOpen(false)} onCreated={handleGrupoCreated} />
      )}
    </div>
  );
}

function Bloque({ n, titulo, icono: Ico, children }) {
  return (
    <section>
      <h3 className="flex items-center gap-2 text-[17px] font-extrabold mb-2.5">
        <span className="w-6 h-6 rounded-full bg-foreground text-background text-[13px] flex items-center justify-center flex-shrink-0">{n}</span>
        {titulo}
      </h3>
      {children}
    </section>
  );
}

function Campo({ etiqueta, icono: Ico, ayuda, children }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-[14px] font-extrabold mb-2">{Ico && <Ico className="h-4 w-4 text-muted-foreground" />}{etiqueta}</label>
      {children}
      {ayuda && <p className="text-[13px] text-muted-foreground mt-1.5">{ayuda}</p>}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 * Pantalla de ÉXITO con links de Drive
 * ════════════════════════════════════════════════════════════════════════ */
function SuccessPane({ tarea, driveReady, onNueva, onClose }) {
  const { toast } = useToast();
  const [t, setT] = useState(tarea);
  const [polling, setPolling] = useState(!driveReady);

  // Si Drive no estaba listo, polling cada 3s hasta 5 intentos
  useEffect(() => {
    if (driveReady) return;
    let count = 0;
    const interval = setInterval(async () => {
      count++;
      try {
        const res = await api.request("GET", `/api/tareas/${tarea.id}`);
        if (res?.drive_brief_url || res?.brief_url) {
          setT(res);
          setPolling(false);
          clearInterval(interval);
        }
      } catch {}
      if (count >= 5) {
        setPolling(false);
        clearInterval(interval);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [tarea.id, driveReady]);

  const briefDoc = t.brief_url || null;  // se setea en backend con doc Ficha Técnica
  const carpetas = [
    { label: "Carpeta de la tarea", url: t.drive_tarea_folder_url },
    { label: "00_BRIEF",            url: t.drive_brief_url },
    { label: "01_GUION",            url: t.drive_guion_url },
    { label: "02_BRUTOS",           url: t.drive_brutos_url },
    { label: "03_EDITABLES",        url: t.drive_editables_url },
    { label: "04_FINALES",          url: t.drive_finales_url },
  ].filter(c => c.url);

  const copy = (url, label) => {
    navigator.clipboard.writeText(url).then(() => toast.success(`${label} copiado`));
  };

  return (
    <div className="p-5 space-y-4">
      {/* Ficha técnica destacada */}
      {briefDoc ? (
        <div className="bg-[#F5F3FF] border border-[#DDD6FE] rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#7C3AED] flex items-center justify-center flex-shrink-0">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold text-[#4C1D95] uppercase tracking-wider">Ficha Técnica</div>
              <div className="text-sm text-foreground mt-0.5 truncate">{briefDoc}</div>
              <div className="flex gap-2 mt-2.5">
                <a href={briefDoc} target="_blank" rel="noopener"
                   className="inline-flex items-center gap-1.5 bg-[#7C3AED] text-white text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-[#5B21B6]">
                  <ExternalLink className="h-3.5 w-3.5" /> Abrir y editar
                </a>
                <button onClick={() => copy(briefDoc, "Brief")}
                        className="inline-flex items-center gap-1.5 bg-white border border-border text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-muted">
                  <Copy className="h-3.5 w-3.5" /> Copiar link
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : polling ? (
        <div className="bg-muted border border-border rounded-xl p-4 flex items-center gap-3">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          <div className="text-sm text-muted-foreground">
            Creando Drive y Ficha Técnica…
            <p className="text-[12px] text-muted-foreground mt-0.5">Esto puede tardar 5-15s</p>
          </div>
        </div>
      ) : (
        <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="h-4 w-4 text-[#D97706] flex-shrink-0" />
          <div className="text-sm text-foreground">
            Drive está tardando más de lo normal. Va a aparecer en la tarea cuando termine.
          </div>
        </div>
      )}

      {/* Lista de carpetas */}
      {carpetas.length > 0 && (
        <div>
          <div className="text-xs font-bold text-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <FolderOpen className="h-3 w-3" /> Carpetas en Drive
          </div>
          <div className="space-y-1">
            {carpetas.map(c => (
              <div key={c.url} className="flex items-center gap-2 text-xs bg-muted rounded-lg p-2 group">
                <span className="flex-1 truncate text-foreground">{c.label}</span>
                <button onClick={() => copy(c.url, c.label)}
                        className="text-muted-foreground hover:text-[#7C3AED] p-1"
                        title="Copiar link">
                  <Copy className="h-3 w-3" />
                </button>
                <a href={c.url} target="_blank" rel="noopener"
                   className="text-[#7C3AED] hover:text-[#5B21B6] p-1" title="Abrir">
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Acciones */}
      <div className="flex items-center gap-2 pt-3 border-t border-border/40">
        <Button variant="ghost" type="button" onClick={onNueva} className="flex-1">
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Crear otra
        </Button>
        <Button type="button" onClick={onClose} className="flex-1">
          Cerrar
        </Button>
      </div>
    </div>
  );
}

/* ── Helpers ───────────────────────────────────────────────────────────── */
function FormField({ label, icon: Icon, children, required }) {
  return (
    <div>
      <label className="flex items-center gap-1 text-[12px] font-medium text-muted-foreground/70 mb-1">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
        {required && <span className="text-[#DC2626] ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const selectCls = "w-full h-12 px-3 rounded-2xl bg-white border-2 border-border text-[16px] text-foreground focus:outline-none focus:border-[#0E7490] transition-colors";

/** Espeja la lógica del backend para mostrar preview en el form. */
function calcFechaLimite(deadlineStr) {
  if (!deadlineStr) return "—";
  const hoy = new Date(Date.now() - 3 * 3600 * 1000).toISOString().slice(0, 10);
  const dl = String(deadlineStr).slice(0, 10);
  if (dl < hoy) return dl;
  const diaHoy = new Date(hoy + "T00:00:00Z");
  const diaDL  = new Date(dl  + "T00:00:00Z");
  const dias = Math.round((diaDL - diaHoy) / 86400000);
  if (dias <= 2) return hoy;
  const lim = new Date(diaDL); lim.setUTCDate(lim.getUTCDate() - 2);
  return lim.toISOString().slice(0, 10);
}
